import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';

import logger from '../../config/app.logger';

import { getExtendedLogger } from '../Logger';

import type { TDatadogUser, TDatadogContext } from './types';
import { IGNORABLE_ERROR_PATTERNS, LOGGER_NAME, GENERIC_CONTEXT } from './constants';

const log = getExtendedLogger(LOGGER_NAME, logger);

function __add_context__(ctxname: string, ctx: TDatadogContext) {
  const globalContext = datadogRum.getGlobalContext();
  return datadogRum.setGlobalContext({ ...globalContext, [ctxname]: ctx });
}

function __set_context__(ctxname: string, ctx: TDatadogContext) {
  const globalContext = datadogRum.getGlobalContext();
  return datadogRum.setGlobalContext({ ...globalContext, [ctxname]: ctx });
}

function updateContext(ctxname: string, ctx: TDatadogContext) {
  log('updating %s datadog context with %o', ctxname, ctx);

  const globalContext = datadogRum.getGlobalContext();
  globalContext[ctxname] ? __set_context__(ctxname, ctx) : __add_context__(ctxname, ctx);

  log('successfully updated %s datadog context', ctxname);
}

function removeContext(ctxname: string) {
  log('removing %s datadog context', ctxname);
  const globalContext = datadogRum.getGlobalContext();
  if (globalContext[ctxname]) {
    delete globalContext[ctxname];
    datadogRum.setGlobalContext(globalContext);
  }
  log('removed %s datadog context', ctxname);
}

function identifyUser(user: TDatadogUser) {
  log('setting datadog user: ', user);
  datadogRum.setUser({
    id: user.id,
    // PII compliance
    // name: user.name,
  });
  log('datadog successfully set');
}

function clearUser() {
  log('removing datadog user');
  datadogRum.clearUser();
  log('datadog user successfully removed');
}

function initDatadog(options: RumInitConfiguration) {
  log('initialising with options %o', options);
  datadogRum.init({
    ...options,
    beforeSend(event, context) {
      if (event.type === 'error' && IGNORABLE_ERROR_PATTERNS.includes(event.error.message)) {
        return false;
      }
      return options.beforeSend?.(event, context) ?? true;
    },
  });
  log('successfully initialised');
}

function updateShellContext(ctxname: string, ctxvalue: string | number | boolean) {
  const globalContext = datadogRum.getGlobalContext();
  const shellContext: any = globalContext[GENERIC_CONTEXT];
  const ctx = { [ctxname]: ctxvalue };
  if (shellContext) {
    __set_context__(GENERIC_CONTEXT, { ...shellContext, ...ctx });
  } else {
    __add_context__(GENERIC_CONTEXT, ctx);
  }
}

function sendActionToDatadog(name: string, context?: object) {
  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
}

function sendCustomTimingToDatadog(name: string, time?: number | undefined) {
  try {
    datadogRum.addTiming(name, time);
  } catch (error) {
    console.log(error); //eslint-disable-line no-console
  }
}

export {
  updateContext,
  identifyUser,
  clearUser,
  removeContext,
  initDatadog,
  updateShellContext,
  sendActionToDatadog,
  sendCustomTimingToDatadog,
};
